import React, { useState, useEffect } from 'react';
import { useStudents, Student } from '../../contexts/StudentsContext';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "../../hooks/use-toast";
import { Loader2, X } from "lucide-react";

interface EditStudentProps {
    student: Student | null;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const EditStudent: React.FC<EditStudentProps> = ({ student, open, onOpenChange }) => {
    const { updateStudent } = useStudents();
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        nom: '',
        prenom: '',
        dateNaissance: '',
        lieuNaissance: '',
        sexe: '' as 'M' | 'F' | '',
        telephone: '',
        email: '',
        adresse: '',
        niveau: '' as '1' | '2' | '3' | '4' | '',
        filiere: '',
        statut: 'actif' as 'actif' | 'inactif' | 'diplome' | 'abandonne',
        anneeAdmission: new Date().getFullYear(),
        photo: '',
    });

    const [documentLinks, setDocumentLinks] = useState<string[]>([]);
    const [documentInput, setDocumentInput] = useState('');

    // Pre-fill form when student changes
    useEffect(() => {
        if (student) {
            setFormData({
                nom: student.nom,
                prenom: student.prenom,
                dateNaissance: new Date(student.dateNaissance).toISOString().split('T')[0],
                lieuNaissance: student.lieuNaissance,
                sexe: student.sexe,
                telephone: student.telephone || '',
                email: student.email || '',
                adresse: student.adresse || '',
                niveau: student.niveau,
                filiere: student.filiere,
                statut: student.statut,
                anneeAdmission: student.anneeAdmission,
                photo: student.photo || '',
            });

            // Extract document links if they exist
            if (student.documents && typeof student.documents === 'object' && 'links' in student.documents) {
                setDocumentLinks((student.documents as any).links || []);
            } else {
                setDocumentLinks([]);
            }
        }
    }, [student]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!student) return;

        if (!formData.nom || !formData.prenom || !formData.dateNaissance || !formData.lieuNaissance ||
            !formData.sexe || !formData.niveau || !formData.filiere) {
            toast({
                title: "Erreur",
                description: "Veuillez remplir tous les champs obligatoires",
                variant: "destructive",
            });
            return;
        }

        setIsSubmitting(true);

        try {
            await updateStudent(student.id, {
                nom: formData.nom,
                prenom: formData.prenom,
                dateNaissance: new Date(formData.dateNaissance),
                lieuNaissance: formData.lieuNaissance,
                sexe: formData.sexe as 'M' | 'F',
                telephone: formData.telephone || undefined,
                email: formData.email || undefined,
                adresse: formData.adresse || undefined,
                niveau: formData.niveau as '1' | '2' | '3' | '4',
                filiere: formData.filiere,
                statut: formData.statut,
                anneeAdmission: formData.anneeAdmission,
                photo: formData.photo || undefined,
                documents: documentLinks.length > 0 ? { links: documentLinks } : undefined,
            });

            toast({
                title: "Succès",
                description: "L'étudiant a été modifié avec succès",
            });

            onOpenChange(false);
        } catch (error: any) {
            toast({
                title: "Erreur",
                description: error.response?.data?.message || "Une erreur est survenue lors de la modification de l'étudiant",
                variant: "destructive",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleAddDocumentLink = () => {
        if (documentInput.trim()) {
            setDocumentLinks([...documentLinks, documentInput.trim()]);
            setDocumentInput('');
        }
    };

    const handleRemoveDocumentLink = (index: number) => {
        setDocumentLinks(documentLinks.filter((_, i) => i !== index));
    };

    const handleDocumentKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddDocumentLink();
        } else if (e.key === ',' && documentInput.trim()) {
            e.preventDefault();
            handleAddDocumentLink();
        }
    };

    if (!student) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Modifier l'étudiant</DialogTitle>
                    <DialogDescription>
                        Modifiez les informations de l'étudiant {student.prenom} {student.nom}.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Nom */}
                        <div className="space-y-2">
                            <Label htmlFor="nom">Nom *</Label>
                            <Input
                                id="nom"
                                value={formData.nom}
                                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                                placeholder="Doe"
                                required
                            />
                        </div>

                        {/* Prénom */}
                        <div className="space-y-2">
                            <Label htmlFor="prenom">Prénom *</Label>
                            <Input
                                id="prenom"
                                value={formData.prenom}
                                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                                placeholder="John"
                                required
                            />
                        </div>

                        {/* Date de naissance */}
                        <div className="space-y-2">
                            <Label htmlFor="dateNaissance">Date de naissance *</Label>
                            <Input
                                id="dateNaissance"
                                type="date"
                                value={formData.dateNaissance}
                                onChange={(e) => setFormData({ ...formData, dateNaissance: e.target.value })}
                                required
                            />
                        </div>

                        {/* Lieu de naissance */}
                        <div className="space-y-2">
                            <Label htmlFor="lieuNaissance">Lieu de naissance *</Label>
                            <Input
                                id="lieuNaissance"
                                value={formData.lieuNaissance}
                                onChange={(e) => setFormData({ ...formData, lieuNaissance: e.target.value })}
                                placeholder="Port-au-Prince"
                                required
                            />
                        </div>

                        {/* Sexe */}
                        <div className="space-y-2">
                            <Label htmlFor="sexe">Sexe *</Label>
                            <Select value={formData.sexe} onValueChange={(value) => setFormData({ ...formData, sexe: value as 'M' | 'F' })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="M">Masculin</SelectItem>
                                    <SelectItem value="F">Féminin</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Téléphone */}
                        <div className="space-y-2">
                            <Label htmlFor="telephone">Téléphone</Label>
                            <Input
                                id="telephone"
                                type="tel"
                                value={formData.telephone}
                                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                                placeholder="+509 1234 5678"
                            />
                        </div>

                        {/* Email */}
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="john.doe@example.com"
                            />
                        </div>

                        {/* Niveau */}
                        <div className="space-y-2">
                            <Label htmlFor="niveau">Niveau *</Label>
                            <Select value={formData.niveau} onValueChange={(value) => setFormData({ ...formData, niveau: value as '1' | '2' | '3' | '4' })}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Sélectionner" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="1">1ère année</SelectItem>
                                    <SelectItem value="2">2ème année</SelectItem>
                                    <SelectItem value="3">3ème année</SelectItem>
                                    <SelectItem value="4">4ème année</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Filière */}
                        <div className="space-y-2">
                            <Label htmlFor="filiere">Filière *</Label>
                            <Input
                                id="filiere"
                                value={formData.filiere}
                                onChange={(e) => setFormData({ ...formData, filiere: e.target.value })}
                                placeholder="Technologie Médicale"
                                required
                            />
                        </div>

                        {/* Année d'admission */}
                        <div className="space-y-2">
                            <Label htmlFor="anneeAdmission">Année d'admission *</Label>
                            <Input
                                id="anneeAdmission"
                                type="number"
                                value={formData.anneeAdmission}
                                onChange={(e) => setFormData({ ...formData, anneeAdmission: parseInt(e.target.value) })}
                                min="2000"
                                max={new Date().getFullYear() + 1}
                                required
                            />
                        </div>

                        {/* Statut */}
                        <div className="space-y-2">
                            <Label htmlFor="statut">Statut</Label>
                            <Select value={formData.statut} onValueChange={(value) => setFormData({ ...formData, statut: value as any })}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="actif">Actif</SelectItem>
                                    <SelectItem value="inactif">Inactif</SelectItem>
                                    <SelectItem value="diplome">Diplômé</SelectItem>
                                    <SelectItem value="abandonne">Abandonné</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Photo URL */}
                    <div className="space-y-2">
                        <Label htmlFor="photo">Photo (URL)</Label>
                        <Input
                            id="photo"
                            type="url"
                            value={formData.photo}
                            onChange={(e) => setFormData({ ...formData, photo: e.target.value })}
                            placeholder="https://example.com/photo.jpg"
                        />
                        <p className="text-xs text-muted-foreground">URL de la photo de l'étudiant</p>
                    </div>

                    {/* Adresse */}
                    <div className="space-y-2">
                        <Label htmlFor="adresse">Adresse</Label>
                        <Textarea
                            id="adresse"
                            value={formData.adresse}
                            onChange={(e) => setFormData({ ...formData, adresse: e.target.value })}
                            placeholder="Adresse complète"
                            rows={3}
                        />
                    </div>

                    {/* Documents */}
                    <div className="space-y-2">
                        <Label htmlFor="documents">Documents (liens)</Label>
                        <div className="space-y-2">
                            <div className="flex gap-2">
                                <Input
                                    id="documents"
                                    type="url"
                                    value={documentInput}
                                    onChange={(e) => setDocumentInput(e.target.value)}
                                    onKeyDown={handleDocumentKeyDown}
                                    placeholder="https://example.com/document.pdf"
                                />
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleAddDocumentLink}
                                    disabled={!documentInput.trim()}
                                >
                                    Ajouter
                                </Button>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                Appuyez sur Entrée ou virgule pour ajouter un lien
                            </p>
                            {documentLinks.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2">
                                    {documentLinks.map((link, index) => (
                                        <div
                                            key={index}
                                            className="flex items-center gap-1 bg-secondary text-secondary-foreground px-2 py-1 rounded-md text-sm"
                                        >
                                            <span className="truncate max-w-[200px]">{link}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveDocumentLink(index)}
                                                className="hover:bg-secondary-foreground/20 rounded-full p-0.5"
                                            >
                                                <X className="h-3 w-3" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Annuler
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Enregistrer
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default EditStudent;
